import { useState, useEffect, useCallback, useRef } from "react";
import { postApi } from "redux/action/api_tbeams";
import { playNewReleaseSound } from "utils/notificationSound";
import { monthAgo, today } from "redux/action/api";

export const usePendingCounts = (userRole) => {
  const [counts, setCounts] = useState({
    internalAudit: 0,
    internalControl: 0,
    pendingApprovals: 0,
    finalApproval: 0,
  });

  // Use ref to track previous counts without causing re-renders
  const previousCountsRef = useRef({
    internalAudit: 0,
    internalControl: 0,
    pendingApprovals: 0,
    finalApproval: 0,
  });

  const fetchPendingCounts = useCallback(async () => {
    try {
      // Fetch counts for each approval stage
      // We fetch based on the current status (filterBy) that each stage is looking for
      const [auditRes, controlRes, pendingRes, finalRes] = await Promise.all([
        // Internal Audit (Internal Control) - fetches releases with "Approval Initiated" status
        postApi(`post_approval_collection`, {
          form: {
            query_type: "select_by_status",
            approval_status: "Approval Initiated",
            date_from: monthAgo,
            date_to: today,
          },
        }),
        // Final Internal Controls - fetches releases with "approvals Reviewed" status
        postApi(`post_approval_collection`, {
          form: {
            query_type: "select_by_status",
            approval_status: "approvals Reviewed",
            date_from: monthAgo,
            date_to: today,
          },
        }),
        // Pending Approvals (Approval Review) - fetches releases with "Final Internal Controls" status
        postApi(`post_approval_collection`, {
          form: {
            query_type: "select_by_status",
            approval_status: "Final Internal Controls",
            date_from: monthAgo,
            date_to: today,
          },
        }),
        // Final Approval - fetches releases with "Approval Released" status
        postApi(`post_approval_collection`, {
          form: {
            query_type: "select_by_status",
            approval_status: "Approval Released",
            date_from: monthAgo,
            date_to: today,
          },
        }),
      ]);

      const newCounts = {
        internalAudit: auditRes?.result?.length || 0,
        internalControl: controlRes?.result?.length || 0,
        pendingApprovals: pendingRes?.result?.length || 0,
        finalApproval: finalRes?.result?.length || 0,
      };

      // Check if any count increased (new release arrived)
      if (
        newCounts.internalAudit > previousCountsRef.current.internalAudit ||
        newCounts.internalControl > previousCountsRef.current.internalControl ||
        newCounts.pendingApprovals >
          previousCountsRef.current.pendingApprovals ||
        newCounts.finalApproval > previousCountsRef.current.finalApproval
      ) {
        playNewReleaseSound();
      }

      // Update ref with new counts
      previousCountsRef.current = newCounts;
      setCounts(newCounts);
    } catch (error) {
      console.error("Error fetching pending counts:", error);
    }
  }, []); // Empty dependency array - function never changes

  useEffect(() => {
    // Fetch immediately
    fetchPendingCounts();

    // Poll every 30 seconds
    const interval = setInterval(fetchPendingCounts, 30000);

    return () => clearInterval(interval);
  }, [fetchPendingCounts]);

  return counts;
};
